import React, { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SOCProvider, useSOC } from "./context/SOCContext";
import Sidebar from "./ui/Sidebar";
import Header from "./ui/Header";
import Dashboard from "./tabs/Dashboard";
import Alerts from "./tabs/Alerts";
// import Assets from "./tabs/Assets";
import Incidents from "./tabs/Incidents";
import Events from "./tabs/Events";
import Investigations from "./tabs/Investigations";
import Response from "./tabs/Response";
import Reports from "./tabs/Reports";
import ThreatIntel from "./tabs/ThreatIntel";

// The SOCProvider is mounted once here, above the tab switch, so
// navigating between tabs never remounts it and never resets the
// scan-simulation timers (see SOCContext.js).
const SOCShell = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [focusId, setFocusId] = useState(null);
  const { setNavigator } = useSOC();

  // Lets a notification deep-link into a specific tab via ?tab=alerts,
  // same convention CSPM already uses.
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const navigate = useCallback((tab, id) => {
    setActiveTab(tab);
    setFocusId(id || null);
  }, []);

  // Give SOCContext a way to trigger navigation from notification clicks.
  React.useEffect(() => {
    setNavigator(navigate);
  }, [setNavigator, navigate]);

  const consumeFocus = useCallback(() => setFocusId(null), []);

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-full mx-auto dark:bg-[#0f172a] min-h-screen box-border">
      <Header onNavigate={navigate} />
      <Sidebar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "dashboard" && <Dashboard onNavigate={navigate} />}
      {activeTab === "alerts" && (
        <Alerts
          focusId={focusId}
          onConsumeFocus={consumeFocus}
          onNavigate={navigate}
        />
      )}
      {/* {activeTab === "assets" && <Assets focusId={focusId} onConsumeFocus={consumeFocus} />} */}
      {activeTab === "incidents" && (
        <Incidents
          focusId={focusId}
          onConsumeFocus={consumeFocus}
          onNavigate={navigate}
        />
      )}
      {activeTab === "events" && <Events />}
      {activeTab === "investigations" && (
        <Investigations
          focusId={focusId}
          onConsumeFocus={consumeFocus}
          onNavigate={navigate}
        />
      )}
      {activeTab === "response" && <Response />}
      {activeTab === "threat-intel" && <ThreatIntel />}
      {activeTab === "reports" && <Reports />}
    </div>
  );
};

const VirtualSOC = () => (
  <SOCProvider>
    <SOCShell />
  </SOCProvider>
);

export default VirtualSOC;
